import { useState, useEffect } from "react";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Download,
  Filter,
  Plus,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { transactionApi, productApi, warehouseApi, exportApi } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterWarehouse, setFilterWarehouse] = useState("all");

  const [formData, setFormData] = useState({
    product_id: "",
    transaction_type: "in",
    quantity: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [txRes, productsRes, warehousesRes] = await Promise.all([
        transactionApi.getAll({ limit: 500 }),
        productApi.getAll(),
        warehouseApi.getAll(),
      ]);
      setTransactions(txRes.data);
      setProducts(productsRes.data);
      setWarehouses(warehousesRes.data);
    } catch (error) {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await transactionApi.create({
        ...formData,
        quantity: parseInt(formData.quantity),
      });
      toast.success(`Stock ${formData.transaction_type === "in" ? "added" : "removed"} successfully`);
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transaction failed");
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: "",
      transaction_type: "in",
      quantity: "",
      notes: "",
    });
  };

  const filteredTransactions = transactions.filter((tx) => {
    const matchesType = filterType === "all" || tx.transaction_type === filterType;
    const matchesWarehouse = filterWarehouse === "all" || tx.warehouse_id === filterWarehouse;
    return matchesType && matchesWarehouse;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate summary stats
  const totalIn = filteredTransactions
    .filter((tx) => tx.transaction_type === "in")
    .reduce((sum, tx) => sum + tx.total, 0);
  const totalOut = filteredTransactions
    .filter((tx) => tx.transaction_type === "out")
    .reduce((sum, tx) => sum + tx.total, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="transactions-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Transactions</h1>
          <p className="text-slate-400">Track inventory movements</p>
        </div>
        <div className="flex gap-3">
          <a
            href={exportApi.transactions()}
            className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded-sm flex items-center gap-2 text-sm btn-transition"
            data-testid="export-transactions-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </a>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-sm flex items-center gap-2 text-sm btn-transition glow-blue"
            data-testid="add-transaction-btn"
          >
            <Plus className="w-4 h-4" />
            New Transaction
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="metric-card flex items-center gap-4">
          <div className="p-3 bg-emerald-500/10 rounded-sm">
            <TrendingUp className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Total Inventory In</p>
            <p className="text-2xl font-bold text-emerald-400 font-mono">${totalIn.toLocaleString()}</p>
          </div>
        </div>
        <div className="metric-card flex items-center gap-4">
          <div className="p-3 bg-red-500/10 rounded-sm">
            <TrendingDown className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Total Inventory Out</p>
            <p className="text-2xl font-bold text-red-400 font-mono">${totalOut.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full md:w-48 bg-[#0B0E14] border-slate-700 text-white" data-testid="type-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-[#151923] border-slate-700">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="in">Inventory In</SelectItem>
            <SelectItem value="out">Inventory Out</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterWarehouse} onValueChange={setFilterWarehouse}>
          <SelectTrigger className="w-full md:w-48 bg-[#0B0E14] border-slate-700 text-white" data-testid="warehouse-filter">
            <SelectValue placeholder="All Warehouses" />
          </SelectTrigger>
          <SelectContent className="bg-[#151923] border-slate-700">
            <SelectItem value="all">All Warehouses</SelectItem>
            {warehouses.map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Transactions Table */}
      {filteredTransactions.length > 0 ? (
        <div className="bg-[#151923] border border-slate-800 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>SKU</th>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                  <th>Warehouse</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx) => (
                  <tr key={tx.id} className="table-row-hover" data-testid={`transaction-row-${tx.id}`}>
                    <td className="text-slate-400 text-xs">{formatDate(tx.created_at)}</td>
                    <td>
                      {tx.transaction_type === "in" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400">
                          <ArrowDownCircle className="w-4 h-4" />
                          IN
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-400">
                          <ArrowUpCircle className="w-4 h-4" />
                          OUT
                        </span>
                      )}
                    </td>
                    <td className="font-mono text-blue-400">{tx.product_sku}</td>
                    <td className="text-slate-200">{tx.product_name}</td>
                    <td className={tx.transaction_type === "in" ? "tx-in" : "tx-out"}>
                      {tx.transaction_type === "in" ? "+" : "-"}{tx.quantity}
                    </td>
                    <td>${tx.unit_price.toFixed(2)}</td>
                    <td className={`font-medium ${tx.transaction_type === "in" ? "text-emerald-400" : "text-red-400"}`}>
                      ${tx.total.toFixed(2)}
                    </td>
                    <td className="text-slate-400">{tx.warehouse_name}</td>
                    <td className="text-slate-500 text-xs max-w-[150px] truncate">{tx.notes || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-[#151923] border border-slate-800 rounded-sm">
          <div className="empty-state">
            <ArrowDownCircle className="empty-state-icon" />
            <p className="text-slate-400 mb-4">No transactions yet</p>
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-500"
            >
              Record First Transaction
            </Button>
          </div>
        </div>
      )}

      {/* New Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#151923] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>New Transaction</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Product</label>
              <Select
                value={formData.product_id}
                onValueChange={(value) => setFormData({ ...formData, product_id: value })}
              >
                <SelectTrigger className="bg-[#0B0E14] border-slate-700" data-testid="tx-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent className="bg-[#151923] border-slate-700 max-h-60">
                  {products.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="font-mono text-blue-400 mr-2">{p.sku}</span>
                      {p.name}
                      <span className="text-slate-500 ml-2">({p.quantity} in stock)</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="form-label">Transaction Type</label>
              <Select
                value={formData.transaction_type}
                onValueChange={(value) => setFormData({ ...formData, transaction_type: value })}
              >
                <SelectTrigger className="bg-[#0B0E14] border-slate-700" data-testid="tx-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#151923] border-slate-700">
                  <SelectItem value="in">
                    <span className="flex items-center gap-2">
                      <ArrowDownCircle className="w-4 h-4 text-emerald-400" />
                      Inventory In (Add Stock)
                    </span>
                  </SelectItem>
                  <SelectItem value="out">
                    <span className="flex items-center gap-2">
                      <ArrowUpCircle className="w-4 h-4 text-red-400" />
                      Inventory Out (Remove Stock)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="form-label">Quantity</label>
              <Input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="bg-[#0B0E14] border-slate-700"
                placeholder="Enter quantity"
                required
                data-testid="tx-quantity-input"
              />
            </div>
            <div>
              <label className="form-label">Notes (Optional)</label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-[#0B0E14] border-slate-700"
                placeholder="Add notes..."
                data-testid="tx-notes-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className={`${formData.transaction_type === "in" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"}`}
                data-testid="tx-submit-btn"
              >
                {formData.transaction_type === "in" ? "Add Stock" : "Remove Stock"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
